import { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ContentLayout,
  Header,
  SpaceBetween,
  Button,
  Container,
  Alert,
} from '@cloudscape-design/components';
import { TemplateGallery } from '@features/configs/components/TemplateGallery';
import { FormJsonToggle, buildJsonFromForm } from '@features/configs/components/FormJsonToggle';
import { ConfigPreview } from '@features/configs/components/ConfigPreview';
import { JsonEditor } from '@features/configs/components/JsonEditor';
import { useCreateConfig } from '@features/configs';
import { useNotifications } from '@features/notifications';
import { getTemplate, templateRegistry, resolveParentPath } from '@features/configs/templates/registry';
import { validateScheduleTimes } from '@features/configs/components/WeeklyScheduleGrid';

type Step = 'select' | 'edit' | 'review';

interface DuplicateState {
  duplicate?: boolean;
  source?: {
    configId: string;
    configName: string;
    configType: string;
    payload: Record<string, unknown>;
  };
}

/**
 * Derives initial state from location state (for duplicate flow).
 */
function getInitialState(locationState: unknown): {
  step: Step;
  configType: string;
  configName: string;
  configId: string;
  formValues: Record<string, unknown>;
} {
  const state = locationState as DuplicateState | null;
  if (state?.duplicate && state.source) {
    // Sanitize configId to valid format (lowercase, numbers, hyphens only)
    const rawId = `${state.source.configId}-copy`;
    const sanitizedId = rawId
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    return {
      step: 'edit',
      configType: state.source.configType,
      configName: `${state.source.configName} (copy)`,
      configId: sanitizedId,
      formValues: { ...state.source.payload },
    };
  }
  return {
    step: 'select',
    configType: '',
    configName: '',
    configId: '',
    formValues: {},
  };
}

/**
 * Configuration create page with three entry points:
 * 1. Create from template (template gallery → guided form)
 * 2. Create from scratch (blank form with configType selector)
 * 3. Import JSON (file upload / paste → form review)
 *
 * Also supports a fourth entry: Duplicate (pre-populated from navigation state).
 *
 * Requirements: 3.1–3.28
 */
export function ConfigCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const createConfig = useCreateConfig();
  const { addNotification } = useNotifications();

  const initial = getInitialState(location.state);

  const [step, setStep] = useState<Step>(initial.step);
  const [configType, setConfigType] = useState(initial.configType);
  /** Registry key for the selected template (may differ from configType for sub-types) */
  const [templateKey, setTemplateKey] = useState(initial.configType);
  const [configName, setConfigName] = useState(initial.configName);
  const [configId, setConfigId] = useState(initial.configId);
  const [formValues, setFormValues] = useState<Record<string, unknown>>(initial.formValues);
  const [showConfigTypeSelector, setShowConfigTypeSelector] = useState(false);
  const [importMode, setImportMode] = useState(false);
  const [importJson, setImportJson] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  // Entry point 1: Select a template (receives registry key, e.g. 'queue-features-callbacks')
  const handleSelectTemplate = (key: string) => {
    const template = templateRegistry.get(key);
    const resolvedConfigType = template?.configType ?? key;
    setTemplateKey(key);
    setConfigType(resolvedConfigType);
    setShowConfigTypeSelector(false);
    setImportMode(false);
    setFormValues({});
    setStep('edit');
  };

  // Entry point 2: Create from scratch
  const handleCreateFromScratch = () => {
    setConfigType('');
    setShowConfigTypeSelector(true);
    setImportMode(false);
    setFormValues({});
    setStep('edit');
  };

  // Entry point 3: Import JSON
  const handleImportJson = () => {
    setImportMode(true);
    setImportJson('');
    setStep('edit');
  };

  // Handle configType change in "from scratch" mode
  const handleConfigTypeChange = (type: string) => {
    setConfigType(type);
    setTemplateKey(type);
    setFormValues({});
  };

  // Validate and move to review step
  const handleProceedToReview = useCallback(() => {
    setValidationError(null);

    // Build the final JSON
    let finalJson: string;
    if (importMode) {
      finalJson = importJson;
    } else {
      finalJson = buildJsonFromForm(configId, configName, configType, formValues);
    }

    // Validate parseable JSON
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(finalJson);
    } catch (e) {
      setValidationError(`Invalid JSON: ${e instanceof Error ? e.message : 'parse error'}`);
      return;
    }

    // Validate required fields
    const missing: string[] = [];
    if (!parsed.configId) missing.push('configId');
    if (!parsed.configName) missing.push('configName');
    if (!parsed.configType) missing.push('configType');
    if (!parsed.payload) missing.push('payload');
    if (missing.length > 0) {
      setValidationError(`Missing required field(s): ${missing.join(', ')}`);
      return;
    }

    // Validate required payload fields based on template schema
    if (!importMode && configType) {
      const template = getTemplate(templateKey || configType);
      if (template?.payloadSchema) {
        const missingPayload: string[] = [];
        for (const field of template.payloadSchema) {
          if (field.required) {
            const val = formValues[field.key];
            if (val === undefined || val === null || val === '') {
              missingPayload.push(field.label);
            }
          }
        }
        if (missingPayload.length > 0) {
          setValidationError(`Required payload field(s) missing: ${missingPayload.join(', ')}`);
          return;
        }
      }
    }

    // Validate business hours time format (if applicable)
    if (!importMode && configType === 'business-hours') {
      const timeErrors = validateScheduleTimes(formValues);
      if (timeErrors.length > 0) {
        setValidationError(`Invalid time format: ${timeErrors.join(' ')}`);
        return;
      }
    }

    // Validate date fields (e.g., holidays startDate/endDate)
    if (!importMode && configType) {
      const template = getTemplate(templateKey || configType);
      if (template?.payloadSchema) {
        const datePattern = /^\d{4}-\d{2}-\d{2}$/;
        const dateErrors: string[] = [];
        for (const field of template.payloadSchema) {
          if (field.type === 'date') {
            const val = formValues[field.key] as string;
            if (val && !datePattern.test(val)) {
              dateErrors.push(`${field.label}: invalid format "${val}". Use YYYY-MM-DD.`);
            }
          }
        }
        if (dateErrors.length > 0) {
          setValidationError(`Invalid date format: ${dateErrors.join(' ')}`);
          return;
        }
      }
    }

    // Validate size
    const size = new Blob([finalJson]).size;
    if (size > 400 * 1024) {
      setValidationError(`Configuration exceeds the 400KB size limit (${Math.round(size / 1024)}KB)`);
      return;
    }

    // If import mode, sync values back
    if (importMode) {
      setConfigId(parsed.configId as string);
      setConfigName(parsed.configName as string);
      setConfigType(parsed.configType as string);
      setFormValues((parsed.payload as Record<string, unknown>) ?? {});
    }

    setStep('review');
  }, [importMode, importJson, configId, configName, configType, formValues]);

  // Submit to API
  const handleSave = async () => {
    const { _rawPayload, _businessUnit, ...payloadFields } = formValues;
    let payload: Record<string, unknown>;
    if (typeof _rawPayload === 'string') {
      try {
        payload = JSON.parse(_rawPayload);
      } catch {
        payload = {};
      }
    } else {
      payload = payloadFields;
    }

    // Inject _parentPath for nested config types (e.g., queue-features sub-types)
    const parentPath = resolveParentPath(templateKey || configType, configType);
    if (parentPath) {
      payload._parentPath = parentPath;
    }

    try {
      await createConfig.mutateAsync({
        configId,
        configName,
        configType,
        payload,
      });
      addNotification('success', `Configuration '${configName}' created successfully`);
      navigate('/');
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      if (message.includes('already exists')) {
        setValidationError(
          'A configuration with this ID already exists. Please choose a different Configuration ID and try again.'
        );
        setStep('edit');
      } else {
        const { translateError } = await import('@features/notifications/error-messages');
        const translated = translateError(e, { configName });
        addNotification('error', translated.message, translated.technicalDetails);
      }
    }
  };

  // Step 1: Template selection
  if (step === 'select') {
    return (
      <ContentLayout>
        <TemplateGallery
          onSelectTemplate={handleSelectTemplate}
          onCreateFromScratch={handleCreateFromScratch}
          onImportJson={handleImportJson}
        />
      </ContentLayout>
    );
  }

  // Step 2: Edit (form/JSON or import)
  if (step === 'edit') {
    return (
      <ContentLayout
        header={
          <Header
            variant="h1"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={() => setStep('select')}>Back</Button>
                <Button variant="primary" onClick={handleProceedToReview}>
                  Review &amp; Save
                </Button>
              </SpaceBetween>
            }
          >
            {importMode ? 'Import JSON' : `Create ${configType || 'Configuration'}`}
          </Header>
        }
      >
        <SpaceBetween size="l">
          {validationError && (
            <Alert type="error" dismissible onDismiss={() => setValidationError(null)}>
              {validationError}
            </Alert>
          )}

          {importMode ? (
            <Container header={<Header variant="h3">Paste or edit JSON</Header>}>
              <JsonEditor value={importJson} onChange={setImportJson} height="500px" />
            </Container>
          ) : (
            <>
              <FormJsonToggle
                configType={templateKey || configType}
                formValues={formValues}
                onFormValuesChange={setFormValues}
                configName={configName}
                onConfigNameChange={setConfigName}
                configId={configId}
                onConfigIdChange={setConfigId}
                showConfigTypeSelector={showConfigTypeSelector}
                onConfigTypeChange={handleConfigTypeChange}
              />
              <ConfigPreview
                configId={configId}
                configName={configName}
                configType={configType}
                formValues={formValues}
              />
            </>
          )}
        </SpaceBetween>
      </ContentLayout>
    );
  }

  // Step 3: Review
  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setStep('edit')}>Back to edit</Button>
              <Button
                variant="primary"
                loading={createConfig.isPending}
                onClick={handleSave}
              >
                Confirm &amp; Save
              </Button>
            </SpaceBetween>
          }
        >
          Review Configuration
        </Header>
      }
    >
      <SpaceBetween size="l">
        {validationError && (
          <Alert type="error" dismissible onDismiss={() => setValidationError(null)}>
            {validationError}
          </Alert>
        )}
        <ConfigPreview
          configId={configId}
          configName={configName}
          configType={configType}
          formValues={formValues}
        />
      </SpaceBetween>
    </ContentLayout>
  );
}
